import { createFileRoute, useParams, useSearch } from '@tanstack/react-router';
import type { JSX } from 'react';

import { ensureQualityEnabled } from '@/features/quality/guard.js';
import { QualityRunDetail } from '@/features/quality/QualityRunDetail.js';
import { parseAgentActionHandoff } from '@/features/quality/run-actions/agentActionHandoff.js';

/**
 * Deep-link search params for a run's report page (S11, AC 26/27): which case is expanded, which
 * recording is bound, and the target timecode — enough to restore "this exact moment" from a
 * shared URL. All three are independent and degrade on their own: a malformed or unreachable value
 * simply falls back to the page's normal (nothing-bound) state instead of crashing or binding the
 * wrong thing. `case` is a reserved word, so it is aliased to `caseId` at every binding site — the
 * URL key itself stays `case`.
 */
export interface QualityRunDeepLinkSearch {
  case?: string;
  rec?: string;
  t?: number;
  agentAction?: string;
  caseIds?: string | unknown[];
  outcomes?: string | unknown[];
  reportRevision?: string | number;
}

/** Trims `case`/`rec` to `undefined` when empty, and accepts `t` only when it parses to a finite,
 *  non-negative number — a hand-edited or stale link degrades field by field rather than as a
 *  whole. Exported so the coercion itself is unit-testable without a router. */
export function parseDeepLinkSearch(
  search: Record<string, unknown>,
): QualityRunDeepLinkSearch {
  const caseId = typeof search.case === 'string' ? search.case.trim() : '';
  const rec = typeof search.rec === 'string' ? search.rec.trim() : '';
  // An empty or whitespace-only `t` (a hand-edited or truncated `?t=`) degrades to `undefined` the
  // same way `case`/`rec` do above — `Number('')` is `0`, which would otherwise read as a genuine
  // "seek to the start" rather than "no timecode given". A real `t=0` still passes through: it never
  // hits this branch, since `search.t` there is the number `0`, not a string.
  const rawT = typeof search.t === 'string' ? search.t.trim() : search.t;
  const t = typeof rawT === 'number' ? rawT : rawT === '' ? NaN : Number(rawT);
  return {
    case: caseId || undefined,
    rec: rec || undefined,
    t: Number.isFinite(t) && t >= 0 ? t : undefined,
    agentAction:
      typeof search.agentAction === 'string' ? search.agentAction : undefined,
    caseIds:
      typeof search.caseIds === 'string'
        ? search.caseIds
        : Array.isArray(search.caseIds)
          ? search.caseIds
          : undefined,
    outcomes:
      typeof search.outcomes === 'string'
        ? search.outcomes
        : Array.isArray(search.outcomes)
          ? search.outcomes
          : undefined,
    reportRevision:
      typeof search.reportRevision === 'string' ||
      typeof search.reportRevision === 'number'
        ? search.reportRevision
        : undefined,
  };
}

export const Route = createFileRoute('/quality_/$id')({
  component: QualityRunPage,
  beforeLoad: () => ensureQualityEnabled(),
  validateSearch: parseDeepLinkSearch,
});

export function QualityRunPage(): JSX.Element {
  const { id } = useParams({ from: '/quality_/$id' });
  const {
    case: caseId,
    rec,
    t,
    agentAction,
    caseIds,
    outcomes,
    reportRevision,
  } = useSearch({
    from: '/quality_/$id',
  });
  const agentHandoff = parseAgentActionHandoff(
    {
      agentAction,
      caseIds,
      outcomes,
      reportRevision,
    },
    window.location.href,
  );
  return (
    <QualityRunDetail
      key={id}
      runId={id}
      caseId={caseId}
      rec={rec}
      t={t}
      agentHandoff={agentHandoff}
    />
  );
}
